import { AttachIcon, CloseIcon, StopIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type SyntheticEvent,
} from "react";

import type { AgentStatus } from "../agent-event.ts";
import {
  ACCEPTED_FILES,
  MAX_UPLOAD_BYTES,
  type UploadedAttachment,
} from "../attachments.ts";
import { readDraft, saveDraft } from "../draft.ts";
import { CHAT_TEXTS as TEXTS } from "../texts.ts";
import { joinTranscript } from "../voice/transcript-text.ts";
import { useVoiceRecorder } from "../voice/use-voice-recorder.ts";
import { DictationField } from "./DictationField.tsx";
import { VoiceControls, VoiceStatus } from "./VoiceInput.tsx";

export function Composer({
  status,
  attachments,
  uploading,
  autoFocus = false,
  onSend,
  onStop,
  onAttach,
  onRemoveAttachment,
}: {
  status: AgentStatus;
  attachments: readonly UploadedAttachment[];
  uploading: boolean;
  /** Se lleva el foco al montarse. Lo pide la ventana pequeña al abrirse. */
  autoFocus?: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onAttach: (file: File) => void;
  onRemoveAttachment: (attachmentId: string) => void;
}) {
  // Lo escrito sobrevive a volver del SSO si la sesión caduca (`draft.ts`).
  const [value, setValue] = useState(readDraft);
  const [error, setError] = useState<string | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = status === "submitted" || status === "streaming";

  /**
   * Lo dictado ENTRA en la caja, no se envía.
   *
   * El usuario lo lee, lo corrige si Whisper le ha entendido mal y decide
   * cuándo mandarlo, igual que si lo hubiera escrito. Y se acumula con lo que
   * ya había: se puede escribir un trozo, dictar otro y seguir escribiendo.
   */
  const insertTranscript = useCallback((text: string) => {
    setValue((current) => joinTranscript(current, text));

    // El foco vuelve a la caja para poder corregir sin buscar el ratón. El
    // tamaño se recalcula después del render, cuando el texto ya está dentro.
    const element = textarea.current;
    if (element === null) return;
    element.focus();
    requestAnimationFrame(() => {
      element.setSelectionRange(element.value.length, element.value.length);
    });
  }, []);

  /**
   * NO se le manda al transcriptor lo que el usuario lleva escrito.
   *
   * Es tentador --sería vocabulario de contexto gratis-- pero `prompt` no es
   * un diccionario: es contexto que sesga la salida, y con audio corto o
   * silencioso estos modelos devuelven parte del prompt como si lo hubieran
   * oído. El caso malo es exactamente el que más se nota: medio mensaje
   * escrito, se pulsa el micrófono, se graba casi silencio, y en la caja
   * aparece una copia de lo que ya había. Parece un bug de duplicación y no
   * hay forma de que el usuario sepa de dónde salió.
   *
   * Cuando haya una lista de términos del cliente --marcas, mecánicas,
   * ciudades-- eso SÍ es lo que pide el parámetro, y la pieza ya lo acepta.
   * Una frase a medias no lo es.
   */
  const voice = useVoiceRecorder({ onText: insertTranscript });

  /**
   * Enviar a media frase CIERRA el dictado y NO envia. Hace falta un segundo
   * Enter.
   *
   * Lo provisional no puede colarse en un mensaje: es texto que el modelo aun
   * puede corregir y que el usuario no ha llegado a leer, y el trato de esta
   * funcion desde el primer dia es que nada se envia sin que lo haya visto.
   *
   * Entre descartarlo y esto, se elige esto: descartar pierde palabras que la
   * persona acaba de decir y no le deja forma de recuperarlas. Asi no se
   * pierde nada -- el dictado se cierra, el texto aterriza en la caja, se lee
   * y se envia. Es ademas lo que ya hace cualquier IME: la primera pulsacion
   * confirma lo que se esta componiendo y la segunda envia.
   */

  useEffect(() => {
    saveDraft(value);
  }, [value]);

  // Al abrir la ventana del chat se escribe, no se busca dónde escribir.
  useEffect(() => {
    if (autoFocus) textarea.current?.focus();
  }, [autoFocus]);

  const submit = (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (voice.active) {
      voice.stop();
      return;
    }
    if (busy || value.trim() === "") return;
    onSend(value);
    setValue("");
    if (textarea.current !== null) textarea.current.style.height = "auto";
  };

  // Enter envía, Shift+Enter hace salto de línea.
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const pickFile = (file: File | undefined) => {
    if (file === undefined) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        TEXTS.composer.tooBig(file.name, MAX_UPLOAD_BYTES / 1024 / 1024),
      );
      return;
    }
    setError(null);
    onAttach(file);
  };

  return (
    // Sin marco ni separador propios: el compositor es la caja de escribir y
    // nada más. Quien lo coloca decide su separación, y son dos sitios muy
    // distintos -- al pie del hilo y en el centro de la pantalla vacía.
    <div className="shrink-0">
      {attachments.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((file) => (
            <li key={file.attachmentId}>
              <Badge variant="secondary" className="gap-1.5 py-1 pr-1 pl-2.5">
                <span className="max-w-48 truncate">{file.fileName}</span>
                <span className="text-muted-foreground">
                  {Math.max(1, Math.round(file.sizeBytes / 1024))} KB
                </span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Quitar ${file.fileName}`}
                  onClick={() => {
                    onRemoveAttachment(file.attachmentId);
                  }}
                >
                  <CloseIcon />
                </Button>
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {uploading && (
        <p className="mb-2 text-sm text-muted-foreground">Subiendo archivo…</p>
      )}
      {error !== null && (
        <p className="mb-2 text-sm text-destructive">{error}</p>
      )}

      <VoiceStatus voice={voice} />

      <form
        onSubmit={submit}
        className="flex items-end gap-2 rounded-lg border bg-card p-2 focus-within:border-ring"
      >
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_FILES}
          hidden
          onChange={(event) => {
            pickFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={TEXTS.composer.attachTitle}
          aria-label={TEXTS.composer.attachLabel}
          disabled={busy || uploading}
          onClick={() => fileInput.current?.click()}
        >
          <AttachIcon />
        </Button>

        <DictationField
          value={value}
          interim={voice.interim}
          disabled={busy}
          placeholder={TEXTS.composer.placeholder}
          textareaRef={textarea}
          onChange={setValue}
          onKeyDown={onKeyDown}
        />

        <VoiceControls voice={voice} disabled={busy} />

        {busy ? (
          <Button type="button" variant="secondary" onClick={onStop}>
            <StopIcon />
            {TEXTS.composer.stop}
          </Button>
        ) : (
          <Button type="submit" disabled={value.trim() === ""}>
            {TEXTS.composer.send}
          </Button>
        )}
      </form>
    </div>
  );
}
